import {ReactNode, useEffect, useMemo, useState} from 'react'
import {Activity, Building2, ChevronLeft, ChevronRight, Database, Download, Eye, FileSpreadsheet, LayoutDashboard, LoaderCircle, RotateCcw, Search, Users} from 'lucide-react'
import {Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts'
import * as XLSX from 'xlsx'
import './ContentAnalytics.scss'

export type ContentKind = 'dashboards' | 'reports'

const loadingMessages = [
  'Requisitando informações na base de dados',
  'Consolidando os acessos do período',
  'Organizando dados do período selecionado',
  'Calculando indicadores e distribuições',
  'Preparando gráficos e detalhamentos',
]

export const ContentAnalyticsLoader = () => {
  const [activeMessage, setActiveMessage] = useState(0)

  useEffect(() => {
    const lastMessage = loadingMessages.length - 1
    if (activeMessage >= lastMessage) return
    const penultimateMessage = loadingMessages.length - 2
    const delay = activeMessage === penultimateMessage ? 1700 * 4 : 1700 * 3
    const timer = window.setTimeout(() => {
      setActiveMessage((current) => Math.min(current + 1, lastMessage))
    }, delay)
    return () => window.clearTimeout(timer)
  }, [activeMessage])

  return (
    <div className='ia-content-loader' role='status' aria-live='polite'>
      <div className='ia-content-loader__orb'><LoaderCircle size={32} /></div>
      <div className='ia-content-loader__eyebrow'>Inteligência em processamento</div>
      <h2>Construindo sua análise</h2>
      <p key={activeMessage}>{loadingMessages[activeMessage]}…</p>
      <div className='ia-content-loader__progress'><span key={`progress-${activeMessage}`} /></div>
    </div>
  )
}

type Props = {
  data: any
  kind: ContentKind
  onKindChange: (kind: ContentKind) => void
}

const colors = ['#3157d5', '#0e9f8a', '#e9a23b', '#7a58c7', '#dd5b69', '#4d91c7']
const number = (value: unknown) => new Intl.NumberFormat('pt-BR').format(Number(value || 0))
const dateTime = (value: unknown) => value
  ? new Intl.DateTimeFormat('pt-BR', {day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'}).format(new Date(String(value)))
  : '—'

const itemName = (row: any, kind: ContentKind) => kind === 'dashboards'
  ? row.dashboard_name || row.log_name || 'Dashboard sem nome'
  : row.content_name || row.log_name || row.log_type_name || 'Relatório sem nome'

const actionType = (row: any) => row.action_type || (/-export$/i.test(row.log_type_name || '') ? 'Exportação' : 'Visualização')

const dayKey = (value: unknown) => {
  const parsed = new Date(String(value || ''))
  return Number.isNaN(parsed.getTime()) ? 'Data desconhecida' : parsed.toISOString().slice(0, 10)
}

const summarize = (rows: any[], kind: ContentKind) => {
  const logs = new Set(rows.map((row) => row.id_log))
  const views = new Set(rows.filter((row) => actionType(row) !== 'Exportação').map((row) => row.id_log))
  const exports = new Set(rows.filter((row) => actionType(row) === 'Exportação').map((row) => row.id_log))
  const users = new Set(rows.map((row) => row.id_user).filter(Boolean))
  const companies = new Set(rows.map((row) => row.id_company).filter(Boolean))
  const items = new Set(rows.map((row) => itemName(row, kind)))
  const evolution = new Map<string, {bucket: string; logs: Set<any>; views: Set<any>; exports: Set<any>; users: Set<any>}>()
  const topItems = new Map<string, {name: string; logs: Set<any>; views: Set<any>; exports: Set<any>; users: Set<any>}>()
  const companyMap = new Map<string, {name: string; logs: Set<any>}>()
  const userMap = new Map<string, {name: string; email: string; logs: Set<any>; views: Set<any>; exports: Set<any>}>()

  rows.forEach((row) => {
    const day = dayKey(row.log_created_at)
    const isExport = actionType(row) === 'Exportação'
    if (!evolution.has(day)) evolution.set(day, {bucket: day, logs: new Set(), views: new Set(), exports: new Set(), users: new Set()})
    evolution.get(day)?.logs.add(row.id_log)
    evolution.get(day)?.[isExport ? 'exports' : 'views'].add(row.id_log)
    evolution.get(day)?.users.add(row.id_user)

    const name = itemName(row, kind)
    if (!topItems.has(name)) topItems.set(name, {name, logs: new Set(), views: new Set(), exports: new Set(), users: new Set()})
    topItems.get(name)?.logs.add(row.id_log)
    topItems.get(name)?.[isExport ? 'exports' : 'views'].add(row.id_log)
    topItems.get(name)?.users.add(row.id_user)

    const company = row.company_name || 'Empresa não identificada'
    if (!companyMap.has(company)) companyMap.set(company, {name: company, logs: new Set()})
    companyMap.get(company)?.logs.add(row.id_log)

    const userKey = String(row.id_user || row.email || row.user_name || 'usuario-nao-identificado')
    if (!userMap.has(userKey)) userMap.set(userKey, {
      name: row.user_name || 'Usuário não identificado',
      email: row.email || '',
      logs: new Set(),
      views: new Set(),
      exports: new Set(),
    })
    userMap.get(userKey)?.logs.add(row.id_log)
    userMap.get(userKey)?.[isExport ? 'exports' : 'views'].add(row.id_log)
  })

  const topUsers = Array.from(userMap.values()).map((entry) => ({name: entry.name, email: entry.email, accesses: entry.logs.size, views: entry.views.size, exports: entry.exports.size}))
    .sort((a, b) => kind === 'reports' ? b.exports - a.exports || b.accesses - a.accesses : b.accesses - a.accesses)
    .slice(0, 10)
  return {
    metrics: {accesses: logs.size, views: views.size, exports: exports.size, users: users.size, companies: companies.size, items: items.size},
    evolution: Array.from(evolution.values()).map((entry) => ({bucket: entry.bucket, accesses: entry.logs.size, views: entry.views.size, exports: entry.exports.size, users: entry.users.size})).sort((a, b) => a.bucket.localeCompare(b.bucket)),
    topItems: Array.from(topItems.values()).map((entry) => ({name: entry.name, accesses: entry.logs.size, views: entry.views.size, exports: entry.exports.size, users: entry.users.size})).sort((a, b) => b.accesses - a.accesses).slice(0, 12),
    companies: Array.from(companyMap.values()).map((entry) => ({name: entry.name, value: entry.logs.size})).sort((a, b) => b.value - a.value).slice(0, 10),
    topUsers,
  }
}

const Card = ({label, value, icon: Icon, tone = 'blue'}: {label: string; value: number; icon: typeof Activity; tone?: string}) => (
  <div className={`ia-metric ia-content-metric ia-content-metric--${tone}`}>
    <div className='ia-metric__top'><span className='ia-metric__icon'><Icon size={16} /></span></div>
    <div className='ia-metric__label'>{label}</div>
    <div className='ia-metric__value'>{number(value)}</div>
  </div>
)

const Panel = ({title, subtitle, children}: {title: string; subtitle?: string; children: ReactNode}) => (
  <section className='ia-panel'>
    <div className='ia-panel__header'><div><h2 className='ia-panel__title'>{title}</h2>{subtitle ? <div className='ia-panel__subtitle'>{subtitle}</div> : null}</div></div>
    {children}
  </section>
)

const exportFiltered = (rows: any[], summary: ReturnType<typeof summarize>, kind: ContentKind) => {
  const workbook = XLSX.utils.book_new()
  const metrics = [
    {Indicador: kind === 'reports' ? 'Interações registradas' : 'Acessos únicos', Valor: summary.metrics.accesses},
    ...(kind === 'reports' ? [
      {Indicador: 'Visualizações de relatórios', Valor: summary.metrics.views},
      {Indicador: 'Exportações de relatórios', Valor: summary.metrics.exports},
    ] : []),
    {Indicador: kind === 'dashboards' ? 'Usuários com acesso a dashboards' : 'Usuários com acesso a relatórios', Valor: summary.metrics.users},
    {Indicador: 'Empresas atuais de referência', Valor: summary.metrics.companies},
    {Indicador: kind === 'dashboards' ? 'Dashboards acessados' : 'Relatórios acessados', Valor: summary.metrics.items},
  ]
  const evolutionRows = summary.evolution.map((entry) => ({
    Data: new Date(entry.bucket + 'T12:00:00').toLocaleDateString('pt-BR'),
    ...(kind === 'reports' ? {'Visualizações': entry.views, 'Exportações': entry.exports} : {'Acessos únicos': entry.accesses}),
    'Usuários com acesso': entry.users,
  }))
  const itemRows = summary.topItems.map((entry) => ({
    [kind === 'dashboards' ? 'Dashboard' : 'Relatório']: entry.name,
    ...(kind === 'reports' ? {'Visualizações': entry.views, 'Exportações': entry.exports} : {'Acessos únicos': entry.accesses}),
    'Usuários com acesso': entry.users,
  }))
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(metrics), 'Resumo')
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(evolutionRows.length ? evolutionRows : [{'Informação': 'Sem dados'}]), 'Evolução')
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(itemRows.length ? itemRows : [{'Informação': 'Sem dados'}]), kind === 'dashboards' ? 'Dashboards' : 'Relatórios')
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summary.topUsers.map((user, index) => ({
    'Posição': index + 1,
    'Nome do Usuário': user.name,
    'E-mail': user.email,
    ...(kind === 'reports' ? {'Visualizações': user.views, 'Exportações': user.exports} : {'Acessos únicos': user.accesses}),
  }))), kind === 'reports' ? 'Top exportadores' : 'Top usuários')
  const accessRows = rows.map((row) => ({
    [kind === 'dashboards' ? 'Dashboard' : 'Relatório']: itemName(row, kind),
    ...(kind === 'reports' ? {'Ação': actionType(row)} : {}),
    'Nome do Usuário': row.user_name || 'Usuário não identificado',
    'E-mail': row.email || '',
    'Empresa atual de referência': row.company_name || 'Não identificada',
    'Tipo da empresa de referência': row.company_type || 'Não identificado',
    'Data e hora': dateTime(row.log_created_at),
  }))
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(accessRows.length ? accessRows : [{'Informação': 'Sem dados'}]), kind === 'reports' ? 'Interações' : 'Acessos')
  const fileKind = kind === 'dashboards' ? 'dashboards' : 'relatorios'
  XLSX.writeFile(workbook, 'analytics-' + fileKind + '-' + new Date().toISOString().slice(0, 10) + '.xlsx')
}

const ContentAnalytics = ({data, kind, onKindChange}: Props) => {
  const [search, setSearch] = useState('')
  const [selectedItem, setSelectedItem] = useState('')
  const [companyType, setCompanyType] = useState('')
  const [actionFilter, setActionFilter] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const rows = useMemo(() => data?.rows || [], [data])

  useEffect(() => {
    setSearch('')
    setSelectedItem('')
    setCompanyType('')
    setActionFilter('')
    setPage(1)
  }, [kind])

  const itemOptions = useMemo(() => Array.from(new Set<string>(rows.map((row: any) => itemName(row, kind)))).sort(), [rows, kind])
  const companyTypes = useMemo(() => Array.from(new Set<string>(rows.map((row: any) => row.company_type).filter(Boolean))).sort(), [rows])
  const filteredRows = useMemo(() => rows.filter((row: any) => {
    const haystack = `${itemName(row, kind)} ${actionType(row)} ${row.user_name || ''} ${row.email || ''} ${row.company_name || ''}`.toLowerCase()
    return (!search || haystack.includes(search.toLowerCase()))
      && (!selectedItem || itemName(row, kind) === selectedItem)
      && (!companyType || row.company_type === companyType)
      && (!actionFilter || actionType(row) === actionFilter)
  }), [rows, kind, search, selectedItem, companyType, actionFilter])
  const summary = useMemo(() => summarize(filteredRows, kind), [filteredRows, kind])
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize))
  const currentPage = Math.min(page, totalPages)
  const firstRow = filteredRows.length ? (currentPage - 1) * pageSize + 1 : 0
  const lastRow = Math.min(currentPage * pageSize, filteredRows.length)
  const pageRows = filteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  const pageNumbers = Array.from(new Set([1, currentPage - 1, currentPage, currentPage + 1, totalPages])).filter((value) => value >= 1 && value <= totalPages).sort((a, b) => a - b)
  const itemLabel = kind === 'dashboards' ? 'Dashboard' : 'Relatório'

  useEffect(() => { setPage(1) }, [search, selectedItem, companyType, actionFilter, pageSize, rows.length])

  return (
    <div className='ia-content'>
      <div className='ia-content-switch' role='tablist' aria-label='Tipo de conteúdo analisado'>
        <button className={kind === 'dashboards' ? 'active' : ''} onClick={() => onKindChange('dashboards')}><LayoutDashboard size={15} /> Dashboards</button>
        <button className={kind === 'reports' ? 'active' : ''} onClick={() => onKindChange('reports')}><Database size={15} /> Relatórios</button>
      </div>

      <section className='ia-content-filters'>
        <div className='ia-content-search'><Search size={14} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder='Buscar usuário, empresa ou conteúdo' /></div>
        <select value={selectedItem} onChange={(event) => setSelectedItem(event.target.value)}><option value=''>Todos — {itemLabel}</option>{itemOptions.map((item) => <option key={item} value={item}>{item}</option>)}</select>
        <select value={companyType} onChange={(event) => setCompanyType(event.target.value)}><option value=''>Todos os tipos de empresa</option>{companyTypes.map((type) => <option key={type} value={type}>{type}</option>)}</select>
        {kind === 'reports' ? <select value={actionFilter} onChange={(event) => setActionFilter(event.target.value)}><option value=''>Todas as ações</option><option value='Visualização'>Visualizações</option><option value='Exportação'>Exportações</option></select> : null}
        <button className='ia-action' onClick={() => {setSearch(''); setSelectedItem(''); setCompanyType(''); setActionFilter(''); setPage(1)}}><RotateCcw size={13} /> Limpar</button>
        <button className='ia-action ia-action--primary' onClick={() => exportFiltered(filteredRows, summary, kind)}><FileSpreadsheet size={13} /> Exportar visão</button>
      </section>

      <div className='ia-scope-note'><strong>Escopo desta seção:</strong> estes indicadores usam logs históricos de conteúdo; em Relatórios, visualizações e exportações são contabilizadas separadamente. A empresa exibida é a referência atual da plataforma. Contas técnicas definidas e perfis Super Admin são desconsiderados.</div>
      {data?.truncated ? <div className='ia-content-warning'>A consulta atingiu o limite de {number(data.max_rows)} registros. Refine o período, a empresa ou o usuário para uma análise integral.</div> : null}

      <div className='ia-metrics ia-content-metrics'>
        <Card label={kind === 'reports' ? 'Interações registradas' : 'Acessos únicos'} value={summary.metrics.accesses} icon={Activity} />
        {kind === 'reports' ? <Card label='Visualizações' value={summary.metrics.views} icon={Eye} tone='green' /> : null}
        {kind === 'reports' ? <Card label='Exportações' value={summary.metrics.exports} icon={Download} tone='amber' /> : null}
        <Card label='Usuários com acesso' value={summary.metrics.users} icon={Users} tone='green' />
        <Card label='Empresas de referência' value={summary.metrics.companies} icon={Building2} tone='purple' />
        <Card label={kind === 'dashboards' ? 'Dashboards acessados' : 'Relatórios acessados'} value={summary.metrics.items} icon={kind === 'dashboards' ? LayoutDashboard : Database} tone='amber' />
      </div>

      {!filteredRows.length ? <div className='ia-empty'><span className='ia-empty__icon'><Database size={22} /></span><h3>Nenhuma interação encontrada</h3><p>Ajuste os filtros ou amplie o período da consulta.</p></div> : <>
        <div className='ia-grid ia-grid--2'>
          <Panel title={kind === 'reports' ? 'Visualizações e exportações por dia' : 'Evolução dos acessos'} subtitle='Usuários são únicos por dia e não devem ser somados entre os dias'>
            <div className='ia-chart'><ResponsiveContainer width='100%' height='100%'><AreaChart data={summary.evolution} margin={{top: 8, right: 8, left: -18, bottom: 0}}><defs><linearGradient id='contentAccessGradient' x1='0' y1='0' x2='0' y2='1'><stop offset='0%' stopColor='#3157d5' stopOpacity='.28' /><stop offset='100%' stopColor='#3157d5' stopOpacity='.02' /></linearGradient></defs><CartesianGrid vertical={false} strokeDasharray='3 3' /><XAxis dataKey='bucket' tickFormatter={(value) => new Date(`${value}T12:00:00`).toLocaleDateString('pt-BR', {day: '2-digit', month: 'short'})} axisLine={false} tickLine={false} /><YAxis allowDecimals={false} axisLine={false} tickLine={false} /><Tooltip /><Legend iconType='circle' iconSize={7} />{kind === 'reports' ? <><Area type='monotone' dataKey='views' name='Visualizações' stroke='#3157d5' fill='url(#contentAccessGradient)' strokeWidth={2} /><Area type='monotone' dataKey='exports' name='Exportações' stroke='#e9a23b' fill='transparent' strokeWidth={2} /></> : <Area type='monotone' dataKey='accesses' name='Acessos únicos' stroke='#3157d5' fill='url(#contentAccessGradient)' strokeWidth={2} />}<Area type='monotone' dataKey='users' name='Usuários com acesso' stroke='#0e9f8a' fill='transparent' strokeWidth={2} /></AreaChart></ResponsiveContainer></div>
          </Panel>
          <Panel title={`Top ${kind === 'dashboards' ? 'dashboards' : 'relatórios'}`} subtitle={kind === 'reports' ? 'Comparativo de visualizações e exportações por relatório' : 'Conteúdos com maior número de acessos no período'}>
            <div className='ia-chart'><ResponsiveContainer width='100%' height='100%'><BarChart data={summary.topItems.slice(0, 8)} layout='vertical' margin={{left: 40, right: 12}}><CartesianGrid horizontal={false} strokeDasharray='3 3' /><XAxis type='number' allowDecimals={false} axisLine={false} tickLine={false} /><YAxis type='category' dataKey='name' width={145} tickFormatter={(value) => String(value).slice(0, 24)} axisLine={false} tickLine={false} /><Tooltip />{kind === 'reports' ? <><Bar dataKey='views' name='Visualizações' fill='#0e9f8a' radius={[0, 6, 6, 0]} maxBarSize={14} /><Bar dataKey='exports' name='Exportações' fill='#e9a23b' radius={[0, 6, 6, 0]} maxBarSize={14} /></> : <Bar dataKey='accesses' name='Acessos únicos' fill='#0e9f8a' radius={[0, 6, 6, 0]} maxBarSize={18} />}</BarChart></ResponsiveContainer></div>
          </Panel>
        </div>
        <div className='ia-grid ia-grid--2'>
          <Panel title='Distribuição por empresa de referência' subtitle='Cada interação é atribuída a uma única empresa atual de referência'>
            <div className='ia-chart ia-chart--compact'><ResponsiveContainer width='100%' height='100%'><PieChart><Pie data={summary.companies} dataKey='value' nameKey='name' innerRadius='50%' outerRadius='74%' paddingAngle={2} stroke='none'>{summary.companies.map((_, index) => <Cell key={index} fill={colors[index % colors.length]} />)}</Pie><Tooltip /><Legend iconType='circle' iconSize={7} /></PieChart></ResponsiveContainer></div>
          </Panel>
          <Panel title={kind === 'reports' ? 'Top 10 exportadores' : 'Top 10 usuários'} subtitle={kind === 'reports' ? 'Usuários que mais exportaram relatórios no período' : 'Usuários com mais acessos únicos no período'}>
            <div className='ia-chart ia-chart--compact'>
              <ResponsiveContainer width='100%' height='100%'>
                <BarChart data={summary.topUsers} layout='vertical' margin={{left: 24, right: 12}}>
                  <CartesianGrid horizontal={false} strokeDasharray='3 3' />
                  <XAxis type='number' allowDecimals={false} axisLine={false} tickLine={false} />
                  <YAxis type='category' dataKey='name' width={130} tickFormatter={(value) => String(value).slice(0, 22)} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Bar dataKey={kind === 'reports' ? 'exports' : 'accesses'} name={kind === 'reports' ? 'Exportações' : 'Acessos únicos'} fill='#3157d5' radius={[0, 6, 6, 0]} maxBarSize={16} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>

        <Panel title={kind === 'dashboards' ? 'Detalhamento de acessos a dashboards' : 'Detalhamento de interações com relatórios'} subtitle={`${number(summary.metrics.accesses)} ${kind === 'reports' ? 'interações' : 'acessos únicos'} · exibindo ${number(firstRow)}–${number(lastRow)}`}>
          <div className='ia-table-wrap'><table className='ia-table'><thead><tr><th>{itemLabel}</th>{kind === 'reports' ? <th>Ação</th> : null}<th>Usuário</th><th>Empresa de referência</th><th>Tipo da empresa</th><th>Data e hora</th></tr></thead><tbody>{pageRows.map((row: any) => <tr key={row.id_log}><td><strong>{itemName(row, kind)}</strong>{kind === 'dashboards' ? <span className='ia-table__path'>ID {row.id_dashboard}</span> : null}</td>{kind === 'reports' ? <td><span className={`ia-method ${actionType(row) === 'Exportação' ? 'ia-method--export' : ''}`}>{actionType(row)}</span></td> : null}<td><div className='ia-user'><div className='ia-user__meta'><strong>{row.user_name || 'Usuário'}</strong><span>{row.email || '—'}</span></div></div></td><td>{row.company_name || '—'}</td><td>{row.company_type || '—'}</td><td>{dateTime(row.log_created_at)}</td></tr>)}</tbody></table></div>
          <div className='ia-pagination'>
            <div className='ia-pagination__size'><span>Itens por página</span><select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>{[10, 25, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}</select></div>
            <div className='ia-pagination__summary'>{number(firstRow)}–{number(lastRow)} de {number(filteredRows.length)}</div>
            <div className='ia-pagination__pages'>
              <button aria-label='Página anterior' disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}><ChevronLeft size={14} /></button>
              {pageNumbers.map((pageNumber, index) => <span key={pageNumber} className='ia-pagination__number-group'>
                {index > 0 && pageNumber - pageNumbers[index - 1] > 1 ? <i>…</i> : null}
                <button className={pageNumber === currentPage ? 'active' : ''} aria-current={pageNumber === currentPage ? 'page' : undefined} onClick={() => setPage(pageNumber)}>{pageNumber}</button>
              </span>)}
              <button aria-label='Próxima página' disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)}><ChevronRight size={14} /></button>
            </div>
          </div>
        </Panel>
      </>}
    </div>
  )
}

export default ContentAnalytics
